import {
  PreScreeningQuestion,
  QuestionType,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  MEASUREMENT_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for AZ Panda Flu Vaccine Study
 * Target population: Healthy adults 65+ with BMI <35
 */
export const azPandaScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "age_eligibility",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "17_or_under",
        label: "17 or under",
        disqualify: true
      },
      {
        value: "18_to_49",
        label: "18 to 49",
        disqualify: true
      },
      {
        value: "50_to_64",
        label: "50 to 64",
        disqualify: true
      },
      {
        value: "65_or_older",
        label: "65 or older"
      },
    ],
    disqualifyMessage: "This study is for adults aged 65 and older.",
    attributeMapping: mappers.value({
      "17_or_under": [],
      "18_to_49": [],
      "50_to_64": [],
      "65_or_older": [],
    }),
  },

  {
    ...standardHeightQuestion,
    attributeMapping: mappers.height({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT }),
  },
  {
    ...standardWeightQuestion,
    maxBMI: 35,
    attributeMapping: mappers.weight({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT }),
  },

  {
    id: "exclusionary_conditions",
    type: QuestionType.CHECKBOX,
    question: "Do you have any of the following health conditions?",
    helpText: "Please select all that apply. If none apply, select 'None of the above'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "bleeding_disorder",
        label: "Bleeding disorder (such as hemophilia)",
        disqualify: true
      },
      {
        value: "penicillin_allergy",
        label: "Allergy to penicillin",
        disqualify: true
      },
      {
        value: "immunocompromised",
        label: "Immunocompromised or taking immunosuppressive medications",
        disqualify: true
      },
      {
        value: "heart_inflammation",
        label: "History of myocarditis (heart muscle inflammation) or pericarditis (heart lining inflammation)",
        disqualify: true
      },
      {
        value: "guillain_barre",
        label: "History of Guillain-Barré syndrome",
        disqualify: true
      },
      {
        value: "multiple_sclerosis",
        label: "Multiple sclerosis (MS)",
        disqualify: true
      },
      {
        value: "cidp",
        label: "Chronic inflammatory demyelinating polyneuropathy (CIDP)",
        disqualify: true
      },
      {
        value: "autoimmune_disease",
        label: "Autoimmune disease (such as lupus, rheumatoid arthritis, or Crohn's disease)",
        disqualify: true
      },
      {
        value: "chronic_kidney_disease",
        label: "Chronic kidney disease",
        disqualify: true
      },
      {
        value: "liver_failure_cirrhosis",
        label: "Liver failure or cirrhosis",
        disqualify: true
      },
      {
        value: "hepatitis_b_c",
        label: "Active hepatitis B or hepatitis C infection",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above"
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants with these conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        bleeding_disorder: [
          { attributeDefinitionId: 15061, booleanAttributeValue: true }, // has_bleedingdisorder
        ],
        penicillin_allergy: [
          { attributeDefinitionId: 22720, booleanAttributeValue: true }, // allergy_penicillin
        ],
        immunocompromised: [
          { attributeDefinitionId: 3052, booleanAttributeValue: true }, // is_immunocompromised
        ],
        heart_inflammation: [
          { attributeDefinitionId: 25888, booleanAttributeValue: true }, // history_myocarditis_pericarditis_prerequisite
        ],
        guillain_barre: [
          { attributeDefinitionId: 25742, booleanAttributeValue: true }, // had_guillianbarre_prerequisite
        ],
        multiple_sclerosis: [
          { attributeDefinitionId: 20328, booleanAttributeValue: true }, // has_ms
        ],
        cidp: [
          { attributeDefinitionId: 26043, booleanAttributeValue: true }, // has_CIDP_prerequisite
        ],
        autoimmune_disease: [
          { attributeDefinitionId: 23135, booleanAttributeValue: true }, // has_autoimmunedisease_e180d
        ],
        chronic_kidney_disease: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: true },
        ],
        liver_failure_cirrhosis: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_CIRRHOSIS, booleanAttributeValue: true },
        ],
        hepatitis_b_c: [
          { attributeDefinitionId: 834, booleanAttributeValue: true }, // has_hepatitisC_active
          { attributeDefinitionId: 15040, booleanAttributeValue: true }, // has_hepatitisB_active
        ],
        none: [],
      },
      noneOfAboveValue: "none",
    }),
  },

  {
    id: "severe_vaccine_allergic_reaction",
    type: QuestionType.RADIO,
    question: "Have you ever had a severe allergic reaction (such as anaphylaxis) to any vaccine?",
    helpText: "This includes reactions like difficulty breathing, swelling of the throat, or requiring emergency medical care.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have had a severe allergic reaction to a vaccine",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not had a severe allergic reaction to a vaccine"
      },
    ],
    disqualifyMessage: "For safety reasons, participants with a history of severe allergic reactions to vaccines cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 23486, booleanAttributeValue: true }, // has_severe_vaccine_allergy
      ],
      no: [
        { attributeDefinitionId: 23486, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "blood_products_6_months",
    type: QuestionType.RADIO,
    question: "Have you received any blood transfusions or blood-related products (such as immunoglobulin) in the past 6 months?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have received blood products in the past 6 months",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not received blood products in the past 6 months"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have received blood products in the past 6 months cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 25074, booleanAttributeValue: true }, // had_blood_products_6_months
      ],
      no: [
        { attributeDefinitionId: 25074, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "pregnancy_breastfeeding",
    type: QuestionType.RADIO,
    question: "Are you currently pregnant or breastfeeding?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I am currently pregnant or breastfeeding",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I am not currently pregnant or breastfeeding"
      },
      {
        value: "not_applicable",
        label: "Not applicable (I am male or cannot become pregnant)"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who are pregnant or breastfeeding cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 26453, booleanAttributeValue: true }, // is_pregnant_prerequisite
        { attributeDefinitionId: 26455, booleanAttributeValue: true }, // is_breastfeeding_prerequisite
      ],
      no: [
        { attributeDefinitionId: 26453, booleanAttributeValue: false },
        { attributeDefinitionId: 26455, booleanAttributeValue: false },
      ],
      not_applicable: [],
    }),
  },

  {
    id: "concurrent_trial",
    type: QuestionType.RADIO,
    question: "Are you currently enrolling in or participating in another clinical trial?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I am not currently enrolling in or participating in another clinical trial"
      },
      {
        value: "yes_recent_medication",
        label: "Yes, I am currently participating in another clinical trial and I have received clinical trial medication in the past 3 months",
        disqualify: true
      },
      {
        value: "yes_follow_up_only",
        label: "Yes, I am participating in the follow-up of a clinical trial but haven't received any trial medication in the past 3 months"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have received clinical trial medication in the past 3 months cannot participate in this study.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
      yes_recent_medication: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
      yes_follow_up_only: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "past_vaccine_study_participation",
    type: QuestionType.RADIO,
    question: "Have you ever participated in a vaccine study before?",
    helpText: "This information helps us understand your experience with vaccine research.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "participated",
        label: "Yes, I have participated in a vaccine study before"
      },
      {
        value: "screened_not_enrolled",
        label: "I have screened for a vaccine study but didn't enroll"
      },
      {
        value: "never_participated",
        label: "No, I have never participated in a vaccine study"
      },
    ],
    attributeMapping: mappers.value({
      participated: [],
      screened_not_enrolled: [],
      never_participated: [],
    }),
  },
];
